import React, { useState, useEffect, useCallback, useRef } from 'react';
import { battlesApi } from '../api';
import { useActivity } from '../App';
import toast from 'react-hot-toast';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default icon paths broken by Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl:       'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const makeIcon = (color) => L.divIcon({
  className: '',
  html: `<div style="width:28px;height:28px;border-radius:50% 50% 50% 0;background:${color};border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);transform:rotate(-45deg)"></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -30],
});

const ICONS = {
  UPCOMING:  makeIcon('#6366f1'),
  ACTIVE:    makeIcon('#22c55e'),
  COMPLETED: makeIcon('#94a3b8'),
  CANCELLED: makeIcon('#ef4444'),
};

// Re-fit map when battles change
const FitBounds = ({ battles }) => {
  const map = useMap();
  useEffect(() => {
    const pts = battles.filter(b => b.venueLatitude && b.venueLongitude)
      .map(b => [b.venueLatitude, b.venueLongitude]);
    if (pts.length > 0) {
      map.fitBounds(pts, { padding: [40, 40], maxZoom: 12 });
    }
  }, [battles, map]);
  return null;
};

// ─── Config ───────────────────────────────────────────────────────────────────
const STATUS_CFG = {
  UPCOMING:  { color: '#6366f1', bg: 'rgba(99,102,241,0.15)',  icon: '🕐', label: 'Upcoming'  },
  ACTIVE:    { color: '#22c55e', bg: 'rgba(34,197,94,0.15)',   icon: '🟢', label: 'Active'    },
  COMPLETED: { color: '#94a3b8', bg: 'rgba(148,163,184,0.15)', icon: '✅', label: 'Completed' },
  CANCELLED: { color: '#ef4444', bg: 'rgba(239,68,68,0.15)',   icon: '❌', label: 'Cancelled' },
};
const getCfg = (s) => STATUS_CFG[(s || '').toUpperCase()] || { color: '#94a3b8', bg: 'rgba(148,163,184,0.15)', icon: '❓', label: s || '—' };

const fmt = (dt) => dt ? new Date(dt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

const downloadCSV = (data) => {
  const headers = ['ID', 'Name', 'Status', 'Entry Type', 'Entry Fee', 'Target KM', 'Max Participants', 'Venue', 'Start Time', 'End Time', 'Created'];
  const rows = data.map(b => [
    b.id, b.name, b.status, b.entryType, b.entryFee ?? 0, b.targetKm ?? 0,
    b.maxParticipants ?? '∞', b.venue || '', fmt(b.startTime), fmt(b.endTime), fmt(b.createdAt),
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'battles.csv'; a.click();
  URL.revokeObjectURL(url);
};

// ─── Participants Modal ────────────────────────────────────────────────────────
const ParticipantsModal = ({ battle, onClose }) => {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    battlesApi.getParticipants(battle.id)
      .then(res => setParticipants(res.data?.data || []))
      .catch(() => toast.error('Failed to load participants'))
      .finally(() => setLoading(false));
  }, [battle.id]);

  return (
    <div style={overlayS}>
      <div style={{ ...modalS, maxWidth: '620px' }}>
        <div style={modalHeaderS}>
          <div>
            <h3 style={{ fontWeight: 700, margin: 0 }}>Participants</h3>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '2px' }}>{battle.name}</div>
          </div>
          <button style={closeBtnS} onClick={onClose}>✕</button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Loading…</div>
        ) : participants.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>👥</div>
            No participants yet
          </div>
        ) : (
          <div style={{ padding: '0 0 16px' }}>
            <div style={{ padding: '8px 20px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              {participants.length} participant{participants.length !== 1 ? 's' : ''}
            </div>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                    {['#', 'User', 'Distance', 'Status', 'Payment', 'Joined'].map(h => (
                      <th key={h} style={{ padding: '9px 16px', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {participants.map((p, i) => (
                    <tr key={p.id} style={{ borderBottom: '1px solid var(--border-color)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={tdS}>{i + 1}</td>
                      <td style={tdS}>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{p.userName || '—'}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>ID: {p.userId}</div>
                      </td>
                      <td style={tdS}>
                        <span style={{ fontWeight: 700, color: '#6366f1' }}>{p.totalDistance?.toFixed(2) ?? '0.00'} km</span>
                      </td>
                      <td style={tdS}>
                        <span style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: '20px',
                          background: p.status === 'ACCEPTED' ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)',
                          color: p.status === 'ACCEPTED' ? '#22c55e' : '#f59e0b', fontWeight: 600 }}>
                          {p.status || '—'}
                        </span>
                      </td>
                      <td style={tdS}>
                        <span style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: '20px',
                          background: p.paymentStatus === 'PAID' ? 'rgba(34,197,94,0.15)' : 'rgba(148,163,184,0.15)',
                          color: p.paymentStatus === 'PAID' ? '#22c55e' : '#94a3b8', fontWeight: 600 }}>
                          {p.paymentStatus || 'FREE'}
                        </span>
                      </td>
                      <td style={{ ...tdS, fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {fmt(p.joinedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Battle Form Modal ─────────────────────────────────────────────────────────
const INITIAL_FORM = {
  name: '', description: '', venue: '', entryType: 'FREE',
  entryFee: '', targetKm: '', maxParticipants: '', normalPrice: '',
  premiumPrice: '', adminCommission: '25', startTime: '', endTime: '',
  isPublic: true, venueLatitude: '', venueLongitude: '',
};

const BattleModal = ({ battle, onClose, onSaved }) => {
  const [form, setForm] = useState(battle ? {
    ...INITIAL_FORM,
    name:            battle.name            || '',
    description:     battle.description     || '',
    venue:           battle.venue           || '',
    entryType:       battle.entryType       || 'FREE',
    entryFee:        battle.entryFee        ?? '',
    targetKm:        battle.targetKm        ?? '',
    maxParticipants: battle.maxParticipants ?? '',
    normalPrice:     battle.normalPrice     ?? '',
    premiumPrice:    battle.premiumPrice    ?? '',
    adminCommission: battle.adminCommission ?? '25',
    startTime:       battle.startTime ? battle.startTime.slice(0,16) : '',
    endTime:         battle.endTime   ? battle.endTime.slice(0,16)   : '',
    isPublic:        battle.isPublic  ?? true,
    venueLatitude:   battle.venueLatitude  ?? '',
    venueLongitude:  battle.venueLongitude ?? '',
  } : INITIAL_FORM);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Battle name is required');
    if (!form.startTime)   return toast.error('Start time is required');
    if (!form.endTime)     return toast.error('End time is required');
    setSaving(true);
    const payload = {
      ...form,
      startTime:      form.startTime      ? form.startTime + ':00'                 : undefined,
      endTime:        form.endTime        ? form.endTime   + ':00'                 : undefined,
      venueLatitude:  form.venueLatitude  ? parseFloat(form.venueLatitude)         : undefined,
      venueLongitude: form.venueLongitude ? parseFloat(form.venueLongitude)        : undefined,
    };
    try {
      if (battle) {
        await battlesApi.update(battle.id, payload);
        toast.success('Battle updated');
      } else {
        await battlesApi.create(payload);
        toast.success('Battle created');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save battle');
    } finally { setSaving(false); }
  };

  const inp = (label, key, type = 'text', placeholder = '') => (
    <div>
      <label style={labelS}>{label}</label>
      <input type={type} placeholder={placeholder} value={form[key]}
        onChange={e => set(key, e.target.value)} style={inputS} />
    </div>
  );

  return (
    <div style={overlayS}>
      <div style={{ ...modalS, maxWidth: '640px' }}>
        <div style={modalHeaderS}>
          <h3 style={{ fontWeight: 700, margin: 0 }}>{battle ? '✏️ Edit Battle' : '➕ Create Battle'}</h3>
          <button style={closeBtnS} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '16px 24px 24px', maxHeight: '75vh', overflowY: 'auto' }}>
          {/* Row 1 */}
          <div style={rowS}>
            {inp('Battle Name *', 'name', 'text', 'e.g. Mumbai Morning Run')}
            {inp('Venue', 'venue', 'text', 'e.g. Juhu Beach, Mumbai')}
          </div>

          {/* Description */}
          <div style={{ marginBottom: '14px' }}>
            <label style={labelS}>Description</label>
            <textarea rows={2} placeholder="Battle details…" value={form.description}
              onChange={e => set('description', e.target.value)}
              style={{ ...inputS, resize: 'vertical', minHeight: '60px' }} />
          </div>

          {/* Row 2 */}
          <div style={rowS}>
            <div>
              <label style={labelS}>Entry Type</label>
              <select value={form.entryType} onChange={e => set('entryType', e.target.value)} style={inputS}>
                <option value="FREE">FREE</option>
                <option value="PAID">PAID</option>
              </select>
            </div>
            {inp('Entry Fee (coins)', 'entryFee', 'number', '0')}
          </div>

          {/* Row 3 */}
          <div style={rowS}>
            {inp('Target KM', 'targetKm', 'number', 'e.g. 5')}
            {inp('Max Participants', 'maxParticipants', 'number', 'leave blank = unlimited')}
          </div>

          {/* Row 4 — Pricing */}
          <div style={rowS}>
            {inp('Normal Price (coins)', 'normalPrice', 'number', '0')}
            {inp('Premium Price (coins)', 'premiumPrice', 'number', '0')}
          </div>

          {/* Row 5 — Commission */}
          <div style={rowS}>
            {inp('Admin Commission %', 'adminCommission', 'number', '25')}
            <div>
              <label style={labelS}>Visibility</label>
              <select value={form.isPublic ? 'true' : 'false'} onChange={e => set('isPublic', e.target.value === 'true')} style={inputS}>
                <option value="true">Public</option>
                <option value="false">Private</option>
              </select>
            </div>
          </div>

          {/* Row 6 — Times */}
          <div style={rowS}>
            {inp('Start Time *', 'startTime', 'datetime-local')}
            {inp('End Time *', 'endTime', 'datetime-local')}
          </div>

          {/* Row 7 — Coordinates for Map */}
          <div style={{ marginBottom: '14px', padding: '12px', background: 'var(--bg-primary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--accent-primary)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              🗺️ Map Coordinates (for map view)
            </div>
            <div style={rowS}>
              {inp('Latitude', 'venueLatitude', 'number', 'e.g. 19.0760')}
              {inp('Longitude', 'venueLongitude', 'number', 'e.g. 72.8777')}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '-8px' }}>
              Tip: Right-click on Google Maps → "What's here?" to get coordinates
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '4px' }}>
            <button style={{ ...btnS, background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }} onClick={onClose}>Cancel</button>
            <button style={{ ...btnS, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', opacity: saving ? 0.6 : 1 }}
              onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : (battle ? '💾 Update' : '🚀 Create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const Battles = () => {
  const { addLog } = useActivity();
  const [battles,  setBattles]  = useState([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [page,     setPage]     = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [viewParticipants, setViewParticipants] = useState(null);
  const [editBattle,       setEditBattle]       = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'map'
  const [allBattles, setAllBattles] = useState([]); // for map — all, no pagination
  const SIZE = 20;

  // Stats
  const [stats, setStats] = useState({ total: 0, upcoming: 0, active: 0, completed: 0 });

  const loadBattles = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, size: SIZE };
      if (statusFilter) params.status = statusFilter;
      const res = await battlesApi.getAll(params);
      const d = res.data;
      setBattles(d.data || []);
      setTotal(d.total || 0);
    } catch { toast.error('Failed to load battles'); }
    finally { setLoading(false); }
  }, [page, statusFilter]);

  const loadStats = async () => {
    try {
      const res = await battlesApi.getAll({ size: 9999 });
      const arr = res.data?.data || [];
      setAllBattles(arr);
      setStats({
        total:     arr.length,
        upcoming:  arr.filter(b => b.status === 'UPCOMING').length,
        active:    arr.filter(b => b.status === 'ACTIVE').length,
        completed: arr.filter(b => b.status === 'COMPLETED').length,
      });
    } catch { /* silent */ }
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { loadBattles(); }, [loadBattles]);

  const handleStatusChange = async (b, newStatus) => {
    try {
      await battlesApi.updateStatus(b.id, newStatus);
      toast.success(`Battle marked ${newStatus}`);
      addLog('Battle Status Changed', `${b.name} → ${newStatus}`, 'update');
      loadBattles(); loadStats();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Delete battle "${b.name}"? This cannot be undone.`)) return;
    try {
      await battlesApi.delete(b.id);
      toast.success('Battle deleted');
      addLog('Battle Deleted', b.name, 'delete');
      loadBattles(); loadStats();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to delete'); }
  };

  const totalPages = Math.ceil(total / SIZE);

  const STAT_CARDS = [
    { label: 'Total Battles', value: stats.total,     color: '#6366f1', icon: '⚔️' },
    { label: 'Upcoming',      value: stats.upcoming,  color: '#6366f1', icon: '🕐' },
    { label: 'Active',        value: stats.active,    color: '#22c55e', icon: '🟢' },
    { label: 'Completed',     value: stats.completed, color: '#94a3b8', icon: '✅' },
  ];

  const STATUS_TABS = ['', 'UPCOMING', 'ACTIVE', 'COMPLETED', 'CANCELLED'];

  return (
    <div className="animate-in">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="breadcrumb"><span>Management</span> / <span className="current">Battles</span></div>
          <h1>⚔️ All Battles</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Create, manage and monitor all walk battles</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {/* View toggle */}
          <div style={{ display: 'flex', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
            {[{ id: 'table', icon: '☰', label: 'Table' }, { id: 'map', icon: '🗺️', label: 'Map' }].map(v => (
              <button key={v.id} onClick={() => setViewMode(v.id)}
                style={{ padding: '8px 16px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem',
                  background: viewMode === v.id ? 'var(--accent-primary)' : 'transparent',
                  color: viewMode === v.id ? 'white' : 'var(--text-secondary)' }}>
                {v.icon} {v.label}
              </button>
            ))}
          </div>
          <button style={exportBtnS} onClick={() => downloadCSV(battles)}>📥 Export CSV</button>
          <button style={primaryBtnS} onClick={() => setEditBattle('new')}>➕ Create Battle</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '24px' }}>
        {STAT_CARDS.map((s, i) => (
          <div key={i} style={cardS}>
            <div style={{ fontSize: '1.6rem' }}>{s.icon}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: s.color, marginTop: '4px' }}>{s.value}</div>
            <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Status filter tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '18px', flexWrap: 'wrap' }}>
        {STATUS_TABS.map(s => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(0); }}
            style={{ padding: '7px 18px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem',
              background: statusFilter === s ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
              color: statusFilter === s ? 'white' : 'var(--text-secondary)' }}>
            {s || 'All'} {s && `(${stats[s.toLowerCase()] ?? 0})`}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', padding: '7px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '20px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          {total} records
        </span>
      </div>

      {/* ── Map View ── */}
      {viewMode === 'map' && (
        <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid var(--border-color)' }}>
          {/* Legend */}
          <div style={{ background: 'var(--bg-card)', padding: '12px 18px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)' }}>LEGEND</span>
            {Object.entries(STATUS_CFG).map(([k, v]) => (
              <span key={k} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: v.color, fontWeight: 600 }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: v.color, display: 'inline-block' }} />
                {v.label}
              </span>
            ))}
            <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              {allBattles.filter(b => b.venueLatitude && b.venueLongitude).length} of {allBattles.length} battles have coordinates
            </span>
          </div>
          <MapContainer
            center={[20.5937, 78.9629]}
            zoom={5}
            style={{ height: '520px', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds battles={allBattles} />
            {allBattles
              .filter(b => b.venueLatitude && b.venueLongitude)
              .map(b => {
                const cfg = getCfg(b.status);
                const icon = ICONS[(b.status || '').toUpperCase()] || ICONS.UPCOMING;
                return (
                  <Marker key={b.id} position={[b.venueLatitude, b.venueLongitude]} icon={icon}>
                    <Popup maxWidth={260}>
                      <div style={{ fontFamily: 'Inter, sans-serif', padding: '4px' }}>
                        <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '6px' }}>{b.name}</div>
                        <div style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 700, background: cfg.bg, color: cfg.color, marginBottom: '8px' }}>
                          {cfg.icon} {cfg.label}
                        </div>
                        <table style={{ width: '100%', fontSize: '0.78rem', borderCollapse: 'collapse' }}>
                          {[
                            ['📍 Venue',   b.venue || '—'],
                            ['🎯 Target',  `${b.targetKm ?? '—'} km`],
                            ['💰 Entry',   b.entryType === 'PAID' ? `🪙 ${b.entryFee}` : 'FREE'],
                            ['⏰ Start',   b.startTime ? new Date(b.startTime).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'2-digit' }) : '—'],
                          ].map(([l, v]) => (
                            <tr key={l}>
                              <td style={{ color: '#64748b', padding: '2px 6px 2px 0', whiteSpace: 'nowrap' }}>{l}</td>
                              <td style={{ fontWeight: 600, padding: '2px 0' }}>{v}</td>
                            </tr>
                          ))}
                        </table>
                        <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                          <button onClick={() => setViewParticipants(b)}
                            style={{ flex: 1, padding: '5px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, background: 'rgba(99,102,241,0.15)', color: '#6366f1' }}>
                            👥 Participants
                          </button>
                          <button onClick={() => setEditBattle(b)}
                            style={{ flex: 1, padding: '5px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, background: 'rgba(99,102,241,0.15)', color: '#6366f1' }}>
                            ✏️ Edit
                          </button>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
          </MapContainer>
          {allBattles.filter(b => !b.venueLatitude || !b.venueLongitude).length > 0 && (
            <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '8px', margin: '12px', padding: '10px 14px', fontSize: '0.8rem', color: '#f59e0b' }}>
              ⚠️ {allBattles.filter(b => !b.venueLatitude || !b.venueLongitude).length} battle(s) missing coordinates — set venueLatitude/venueLongitude when creating battles to show them on the map.
            </div>
          )}
        </div>
      )}

      {/* Table */}
      {viewMode === 'table' && <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                {['#', 'Battle', 'Status', 'Entry', 'Target', 'Participants', 'Start Time', 'End Time', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '50px', color: 'var(--text-muted)' }}>Loading…</td></tr>
              ) : battles.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '50px' }}>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No battles found</div>
                  <button style={{ ...primaryBtnS, marginTop: '12px' }} onClick={() => setEditBattle('new')}>➕ Create First Battle</button>
                </td></tr>
              ) : battles.map((b, i) => {
                const cfg = getCfg(b.status);
                return (
                  <tr key={b.id}
                    style={{ borderBottom: '1px solid var(--border-color)', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={tdS}>{page * SIZE + i + 1}</td>
                    <td style={tdS}>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                        {b.venue ? `📍 ${b.venue}` : '📍 No venue'}
                      </div>
                    </td>
                    <td style={tdS}>
                      <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: 700, background: cfg.bg, color: cfg.color }}>
                        {cfg.icon} {cfg.label}
                      </span>
                    </td>
                    <td style={tdS}>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                        {b.entryType === 'PAID' ? `🪙 ${b.entryFee ?? 0}` : <span style={{ color: '#22c55e' }}>FREE</span>}
                      </div>
                      {b.entryType === 'PAID' && b.adminCommission && (
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{b.adminCommission}% commission</div>
                      )}
                    </td>
                    <td style={tdS}>
                      <span style={{ fontWeight: 700, color: '#6366f1' }}>{b.targetKm ?? '—'} km</span>
                    </td>
                    <td style={{ ...tdS, textAlign: 'center' }}>
                      <button style={{ padding: '4px 12px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, background: 'rgba(99,102,241,0.12)', color: '#6366f1' }}
                        onClick={() => setViewParticipants(b)}>
                        👥 View
                      </button>
                    </td>
                    <td style={{ ...tdS, fontSize: '0.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmt(b.startTime)}</td>
                    <td style={{ ...tdS, fontSize: '0.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmt(b.endTime)}</td>
                    <td style={tdS}>
                      <div style={{ display: 'flex', gap: '5px', flexWrap: 'nowrap' }}>
                        {/* Status quick-actions */}
                        {b.status === 'UPCOMING'  && <button style={smBtnS('#22c55e')} onClick={() => handleStatusChange(b, 'ACTIVE')}>▶ Start</button>}
                        {b.status === 'ACTIVE'     && <button style={smBtnS('#94a3b8')} onClick={() => handleStatusChange(b, 'COMPLETED')}>✔ End</button>}
                        {b.status !== 'CANCELLED'  && b.status !== 'COMPLETED' && (
                          <button style={smBtnS('#f59e0b')} onClick={() => handleStatusChange(b, 'CANCELLED')}>✕ Cancel</button>
                        )}
                        {/* Edit */}
                        <button style={smBtnS('#6366f1')} onClick={() => setEditBattle(b)}>✏️</button>
                        {/* Delete */}
                        <button style={smBtnS('#ef4444')} onClick={() => handleDelete(b)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid var(--border-color)' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {page * SIZE + 1}–{Math.min((page + 1) * SIZE, total)} of {total}
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
                style={{ ...btnS, padding: '5px 12px', opacity: page === 0 ? 0.4 : 1 }}>‹</button>
              <span style={{ padding: '5px 12px', background: 'var(--accent-primary)', color: 'white', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 600 }}>
                {page + 1} / {totalPages}
              </span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
                style={{ ...btnS, padding: '5px 12px', opacity: page >= totalPages - 1 ? 0.4 : 1 }}>›</button>
            </div>
          </div>
        )}
      </div>}

      {/* Modals */}
      {viewParticipants && (
        <ParticipantsModal battle={viewParticipants} onClose={() => setViewParticipants(null)} />
      )}
      {editBattle && (
        <BattleModal
          battle={editBattle === 'new' ? null : editBattle}
          onClose={() => setEditBattle(null)}
          onSaved={() => { loadBattles(); loadStats(); }}
        />
      )}
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const overlayS = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px',
};
const modalS = {
  background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
  borderRadius: '16px', width: '100%', maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
};
const modalHeaderS = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '18px 24px', borderBottom: '1px solid var(--border-color)',
};
const closeBtnS = {
  background: 'none', border: 'none', color: 'var(--text-muted)',
  cursor: 'pointer', fontSize: '1.2rem', padding: '4px', flexShrink: 0,
};
const cardS = {
  background: 'var(--bg-card)', border: '1px solid var(--border-color)',
  borderRadius: '12px', padding: '18px', textAlign: 'center',
};
const tdS = { padding: '10px 14px', fontSize: '0.85rem' };
const btnS = {
  padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--border-color)',
  cursor: 'pointer', background: 'var(--bg-tertiary)', color: 'var(--text-primary)',
  fontSize: '0.85rem', fontWeight: 600,
};
const primaryBtnS = {
  padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer',
  fontSize: '0.88rem', fontWeight: 700,
  background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white',
};
const exportBtnS = {
  padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer',
  fontSize: '0.88rem', fontWeight: 600,
  background: 'rgba(99,102,241,0.15)', color: 'var(--accent-primary)',
};
const inputS = {
  width: '100%', padding: '9px 12px', background: 'var(--bg-primary)',
  border: '1px solid var(--border-color)', borderRadius: '8px',
  color: 'var(--text-primary)', fontSize: '0.88rem', outline: 'none',
  boxSizing: 'border-box',
};
const labelS = { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.04em' };
const rowS = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' };
const smBtnS = (color) => ({
  padding: '4px 9px', borderRadius: '6px', border: 'none', cursor: 'pointer',
  fontSize: '0.72rem', fontWeight: 700,
  background: `${color}22`, color,
});

export default Battles;
